import type {PhoneInputOption, PhoneInputValue} from './types';

const PREFIX_REGEX = /^\+/;

type PhoneInputValueInput = Partial<
  Record<keyof PhoneInputValue, string | number | null | undefined>
>;

function isPhoneInputValue(
  value: string | number | PhoneInputValueInput | null | undefined,
): value is PhoneInputValueInput {
  if (typeof value === 'string' || typeof value === 'number') {
    return false;
  }

  return Boolean(value && ('code' in value || 'number' in value));
}

function getPhoneValuePart(value: unknown): string | undefined {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  return undefined;
}

export function toPhoneValue(
  value: string | number | PhoneInputValue | null | undefined,
  fallbackCode?: string,
  fallbackNumber?: string,
): PhoneInputValue {
  if (value === null || value === undefined) {
    return {code: fallbackCode ?? '', number: fallbackNumber ?? ''};
  }

  if (isPhoneInputValue(value)) {
    return {
      code: getPhoneValuePart(value.code) ?? fallbackCode ?? '',
      number: getPhoneValuePart(value.number) ?? fallbackNumber ?? '',
    };
  }

  return {code: fallbackCode ?? '', number: fallbackNumber ?? String(value)};
}

export function clearPhoneNumber(value: string): string {
  return value.replace(/[^0-9]/g, '');
}

export function findPhoneCode(value: string, codeSet: Set<string>): string | undefined {
  const hasPrefix = PREFIX_REGEX.test(value);
  const maxLength = hasPrefix ? 4 : 3;
  const minLength = hasPrefix ? 2 : 1;

  for (let length = minLength; length <= maxLength; length += 1) {
    const candidate = value.slice(0, length);
    const normalized = hasPrefix ? candidate : `+${candidate}`;
    if (codeSet.has(normalized)) {
      return normalized;
    }
  }

  return undefined;
}

export function parsePhoneValueWithOptions(options: PhoneInputOption[]) {
  const codeSet = new Set(options.map(option => option.value));

  return (value: string): PhoneInputValue => {
    if (!PREFIX_REGEX.test(value)) {
      return {code: '', number: value};
    }

    const code = findPhoneCode(value, codeSet);
    if (!code) {
      return {code: '', number: value};
    }

    return {
      code,
      number: value.slice(code.length),
    };
  };
}

export function findPhoneCodeOption(options: PhoneInputOption[], code: string) {
  return options.find(option => option.value === code);
}

export function formatPhoneCodeOptionLabel(option: PhoneInputOption): string {
  const label = String(option.label);
  const value = String(option.value);
  return label.includes(value) ? label : `${label} ${value}`;
}
